import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Rede_PainelResponse';
import '../contracts/Rede_UnidadesResponse';
import '../contracts/Rede_HistoricoResponse';
import '../contracts/Rede_SituacaoInput';
import '../contracts/Rede_PastaInput';
import '../contracts/Rede_FichaInput';
import '../contracts/Rede_FichaResponse';
import '../contracts/Rede_EncerramentosResponse';
import '../contracts/Rede_EncerramentoResponse';
import '../contracts/Rede_EncerramentoInput';
import '../contracts/Rede_EncerramentoConcluirInput';

import type { Rede_PainelResponseDados } from '../contracts/Rede_PainelResponse';
import type { Rede_UnidadeRow } from '../contracts/Rede_UnidadeRow';
import type { Rede_HistoricoResponseDados } from '../contracts/Rede_HistoricoResponse';
import type { Rede_SituacaoInput } from '../contracts/Rede_SituacaoInput';
import type { Rede_PastaInput } from '../contracts/Rede_PastaInput';
import type { Rede_FichaInput } from '../contracts/Rede_FichaInput';
import type { Rede_FichaResponseDados } from '../contracts/Rede_FichaResponse';
import type { Rede_EncerramentosResponseDados } from '../contracts/Rede_EncerramentosResponse';
import type { Rede_EncerramentoResponseDados } from '../contracts/Rede_EncerramentoResponse';
import type { Rede_EncerramentoInput } from '../contracts/Rede_EncerramentoInput';
import type { Rede_EncerramentoConcluirInput } from '../contracts/Rede_EncerramentoConcluirInput';

/**
 * Base de unidades da rede (chamado NS 2026-0082, entrega 1; Full): o painel geral, a lista
 * de unidades com a situação, o histórico e a pasta no Drive de cada uma, e a importação da
 * planilha "Base de Unidades" (ensaio e aplicar), a ficha de cada unidade com os sócios e o encerramento com o checklist
 * (entrega 4). As regras (situação efetiva, o que conta em cada total, o que impede concluir) moram no app-api.
 */
const URL_PAINEL = '/index.php/rede/painel';
const URL_UNIDADES = '/index.php/rede/unidades';
const URL_HISTORICO = '/index.php/rede/historico';
const URL_SITUACAO = '/index.php/rede/situacao';
const URL_PASTA = '/index.php/rede/pasta';
const URL_FICHA = '/index.php/rede/ficha';
const URL_ENCERRAMENTOS = '/index.php/rede/encerramentos';
const URL_ENCERRAMENTO = '/index.php/rede/encerramento';
const URL_CONCLUIR = '/index.php/rede/encerramento/concluir';

/** As situações de um item do checklist de encerramento; as chaves são as do app-api. */
export const SITUACOES_DO_ITEM: { chave: string; rotulo: string }[] = [
  { chave: 'pendente', rotulo: 'Pendente' },
  { chave: 'feito', rotulo: 'Feito' },
  { chave: 'nao_feito', rotulo: 'Não devolvido' },
  { chave: 'judicial', rotulo: 'Em processo judicial' },
  { chave: 'nao_se_aplica', rotulo: 'Não se aplica' },
];

/** As situações, na ordem dos cartões, com o rótulo de UMA unidade. */
export const SITUACOES: { chave: string; rotulo: string; cor: string }[] = [
  { chave: 'ativa', rotulo: 'Ativa', cor: '#1e7b4f' },
  { chave: 'em_onboarding', rotulo: 'Em onboarding', cor: '#1a3e9e' },
  { chave: 'a_conferir', rotulo: 'A conferir', cor: '#9a6700' },
  { chave: 'repassada', rotulo: 'Repassada', cor: '#6b4fbb' },
  { chave: 'encerrada', rotulo: 'Encerrada', cor: '#8c1d13' },
  { chave: 'desistiu', rotulo: 'Desistiu', cor: '#5b6168' },
];

const MOTIVOS: Record<string, string> = {
  situacao_invalida: 'Escolha a situação.',
  data_invalida: 'Informe a data.',
  data_no_futuro: 'A data não pode ser no futuro.',
  situacao_igual_a_atual: 'A unidade já está nessa situação.',
  franquia_nao_encontrada: 'Unidade não encontrada.',
  unidade_nao_encontrada: 'Unidade não encontrada.',
  campo_desconhecido: 'Campo da ficha desconhecido.',
  socio_sem_nome: 'Todo sócio precisa de nome.',
  socios_demais: 'No máximo 10 sócios.',
  cpf_invalido: 'CPF inválido.',
  email_invalido: 'E-mail inválido.',
  falha_ao_gravar: 'Não foi possível gravar. Tente de novo.',
  link_da_pasta_invalido: 'Cole o link de uma pasta do Google Drive (https://drive.google.com/…).',
  encerramento_nao_encontrado: 'Encerramento não encontrado.',
  encerramento_ja_aberto: 'Esta unidade já tem um encerramento em andamento.',
  encerramento_concluido: 'Este encerramento já foi concluído.',
  encerramento_sem_unidade: 'Escolha a unidade do encerramento antes de concluir.',
  item_desconhecido: 'Item do checklist desconhecido.',
  situacao_do_item_invalida: 'Situação do item inválida.',
  itens_pendentes: 'Resolva todos os itens: feito, não se aplica, ou com a observação de por que não.',
  destino_invalido: 'Escolha se a unidade foi encerrada ou repassada.',
  novo_franqueado_obrigatorio: 'Informe o novo franqueado do repasse.',
};

export const mensagemDaRede = (e: unknown): string => {
  const bruto = e instanceof Error ? e.message : String(e);
  return MOTIVOS[bruto] ?? bruto;
};

export function useRede() {
  const painel = ref<Rede_PainelResponseDados | null>(null);
  const unidades = ref<Rede_UnidadeRow[]>([]);
  const historico = ref<Rede_HistoricoResponseDados | null>(null);
  const carregando = ref(false);

  const executar = async <T>(onde: string, fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      logError('useRede', onde, 'Erro', e);
      throw e;
    }
  };

  /** O painel e a lista juntos — a tela usa os dois. */
  const carregar = async (): Promise<void> => {
    carregando.value = true;
    try {
      await executar('carregar', async () => {
        const [p, u] = await Promise.all([
          httpService.get(URL_PAINEL, { responseContract: 'Rede_PainelResponse' }),
          httpService.get(URL_UNIDADES, { responseContract: 'Rede_UnidadesResponse' }),
        ]);
        if (!p.success || !p.dados) throw new Error(p.error || 'Erro ao carregar o painel');
        if (!u.success || !u.dados) throw new Error(u.error || 'Erro ao carregar as unidades');
        painel.value = p.dados;
        unidades.value = u.dados.unidades;
        log('useRede', 'carregar', 'unidades', u.dados.total);
      });
    } finally {
      carregando.value = false;
    }
  };

  const carregarHistorico = (franquiaId: number): Promise<void> =>
    executar('carregarHistorico', async () => {
      const r = await httpService.get(`${URL_HISTORICO}/${franquiaId}`, { responseContract: 'Rede_HistoricoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar o histórico');
      historico.value = r.dados;
    });

  const mudarSituacao = (dados: Rede_SituacaoInput): Promise<void> =>
    executar('mudarSituacao', async () => {
      const r = await httpService.post(URL_SITUACAO, dados, { inputContract: 'Rede_SituacaoInput', responseContract: 'Rede_HistoricoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao mudar a situação');
      historico.value = r.dados;
    });

  const gravarPasta = (dados: Rede_PastaInput): Promise<void> =>
    executar('gravarPasta', async () => {
      const r = await httpService.post(URL_PASTA, dados, { inputContract: 'Rede_PastaInput', responseContract: 'Rede_HistoricoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao gravar a pasta');
      historico.value = r.dados;
    });


  /** unidade = "f:<franquia_id>" | "e:<id da unidade fora do sistema>" */
  const carregarFicha = (unidade: string): Promise<Rede_FichaResponseDados> =>
    executar('carregarFicha', async () => {
      const [tipo, id] = unidade.split(':');
      const r = await httpService.get(`${URL_FICHA}/${tipo}/${id}`, { responseContract: 'Rede_FichaResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar a ficha');
      return r.dados;
    });

  const salvarFicha = (dados: Rede_FichaInput): Promise<Rede_FichaResponseDados> =>
    executar('salvarFicha', async () => {
      const r = await httpService.post(URL_FICHA, dados, { inputContract: 'Rede_FichaInput', responseContract: 'Rede_FichaResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao gravar a ficha');
      return r.dados;
    });

  const carregarUnidades = (): Promise<Rede_UnidadeRow[]> =>
    executar('carregarUnidades', async () => {
      const r = await httpService.get(URL_UNIDADES, { responseContract: 'Rede_UnidadesResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar as unidades');
      return r.dados.unidades;
    });

  const carregarEncerramentos = (): Promise<Rede_EncerramentosResponseDados> =>
    executar('carregarEncerramentos', async () => {
      const r = await httpService.get(URL_ENCERRAMENTOS, { responseContract: 'Rede_EncerramentosResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar os encerramentos');
      return r.dados;
    });

  const carregarEncerramento = (id: number): Promise<Rede_EncerramentoResponseDados> =>
    executar('carregarEncerramento', async () => {
      const r = await httpService.get(`${URL_ENCERRAMENTO}/${id}`, { responseContract: 'Rede_EncerramentoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar o encerramento');
      return r.dados;
    });

  /** id 0 abre um encerramento para a unidade; com id grava o checklist (e casa, com unidade). */
  const salvarEncerramento = (dados: Rede_EncerramentoInput): Promise<Rede_EncerramentoResponseDados> =>
    executar('salvarEncerramento', async () => {
      const r = await httpService.post(URL_ENCERRAMENTO, dados, { inputContract: 'Rede_EncerramentoInput', responseContract: 'Rede_EncerramentoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao gravar o encerramento');
      return r.dados;
    });

  const concluirEncerramento = (dados: Rede_EncerramentoConcluirInput): Promise<Rede_EncerramentoResponseDados> =>
    executar('concluirEncerramento', async () => {
      const r = await httpService.post(URL_CONCLUIR, dados, { inputContract: 'Rede_EncerramentoConcluirInput', responseContract: 'Rede_EncerramentoResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao concluir o encerramento');
      log('useRede', 'concluirEncerramento', r.dados.destino, r.dados.id);
      return r.dados;
    });

  return {
    painel, unidades, historico, carregando, carregar, carregarHistorico, mudarSituacao, gravarPasta, carregarFicha, salvarFicha,
    carregarUnidades, carregarEncerramentos, carregarEncerramento, salvarEncerramento, concluirEncerramento,
  };
}
